'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type FC,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { ChevronRight, SlidersHorizontal, X } from 'lucide-react';

import { cn } from '@/utils/cn';
import { AnimatedList, AnimatedListItem } from '@/components/animations';
import { useDevice } from '@/context/device-context';

/**
 * One form, two homes (spec 013, R4).
 *
 * The filter form is rendered ONCE, as this component's child, and this wrapper
 * decides where it lives: a panel pinned in the left column from `lg` up, or an
 * off-canvas dialog below it. The switch is CSS (`lg:` classes) for presentation
 * and `useDevice()` for behaviour — the device context splits at exactly 1024px,
 * the same as `lg`, and is seeded from the user agent on the server, so the
 * server renders the right home and there is no flash.
 *
 * The form is never remounted, so a visitor who edits a field and then resizes
 * across the breakpoint keeps what they typed. That rules out AnimatePresence
 * (conditional mount) and rendering the form twice (duplicate control ids).
 *
 * Pinning is not this component's job: the listing wraps header and form in
 * one `StickyPanel`, the same as the lot page, so the two pages scroll alike.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type TAuctionFilterHomeContext = {
  /** Closes the drawer. A no-op in sidebar mode. */
  close: () => void;
};

const AuctionFilterHomeContext = createContext<TAuctionFilterHomeContext | null>(null);

/** Lets the form close the drawer after Szukaj / Wyczyść without the listing wiring anything. */
export const useAuctionFilterHome = () => useContext(AuctionFilterHomeContext);

type TAuctionFilterHome = {
  /** Applied filters, for the trigger's count. */
  activeCount: number;
  children: ReactNode;
};

const activeFiltersLabel = (count: number): string => {
  if (count === 1) return '1 aktywny filtr';
  const few = count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 12 || count % 100 > 14);
  return `${count} ${few ? 'aktywne filtry' : 'aktywnych filtrów'}`;
};

export const AuctionFilterHome: FC<TAuctionFilterHome> = ({ activeCount, children }) => {
  const { deviceType } = useDevice();
  const isSidebar = deviceType === 'desktop';
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setIsOpen(wasOpen => {
      // Focus goes back where it came from — but only if the drawer was open,
      // so the form's `close()` after Szukaj is harmless in sidebar mode.
      if (wasOpen) triggerRef.current?.focus();

      return false;
    });
  }, []);

  // Body scroll lock while open (the header's mobile menu does the same), and
  // focus moves into the dialog once it is visible.
  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    const frame = requestAnimationFrame(() => closeRef.current?.focus());

    return () => {
      cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Growing past the breakpoint while open: the sidebar takes over and the
  // lock must not linger.
  useEffect(() => {
    if (isSidebar && isOpen) setIsOpen(false);
  }, [isSidebar, isOpen]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // An open react-select menu handles Escape itself (and prevents default);
    // that press closes the menu, not the drawer.
    if (isSidebar || event.defaultPrevented) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      close();

      return;
    }

    if (event.key !== 'Tab' || !panelRef.current) return;

    const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      element => element.offsetParent !== null
    );

    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <AuctionFilterHomeContext.Provider value={{ close }}>
      {/* The drawer's opener, in the cards' family rather than a stock button:
          a dark bar with the icon in a tile, the label over a hint, and a gold
          end cap cut by the same seam as the pagination and the photo tags. The
          cap carries the active count — the one thing a visitor needs before
          opening — or a chevron when nothing is applied. */}
      <AnimatedList>
        <AnimatedListItem>
          <button
            ref={triggerRef}
            type="button"
            onClick={() => setIsOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            aria-controls="auction-filters-panel"
            className="group relative flex w-full items-stretch overflow-hidden rounded-xl border border-neutral-800 bg-backgroundLight text-left transition-colors hover:border-action/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action/60 lg:hidden"
          >
            <span className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-neutral-800 bg-background text-action transition-colors group-hover:border-action/40">
                <SlidersHorizontal size={18} aria-hidden />
              </span>
              <span className="flex min-w-0 flex-col">
                <span className="font-oswald text-lg uppercase leading-tight text-foreground">Filtry</span>
                <span className="truncate font-roboto text-xs text-neutral-500">
                  {activeCount > 0 ? activeFiltersLabel(activeCount) : 'Marka, model, rocznik, cena, stan'}
                </span>
              </span>
            </span>
            <span
              aria-hidden
              className="relative -ml-3 flex w-[4.5rem] shrink-0 items-center justify-center bg-action pl-3 font-oswald text-2xl text-background transition-colors [clip-path:polygon(18px_0,100%_0,100%_100%,0_100%)] group-hover:bg-[#fcc61f]"
            >
              {activeCount > 0 ? activeCount : <ChevronRight size={22} />}
            </span>
          </button>
        </AnimatedListItem>
      </AnimatedList>

      {!isSidebar && (
        <div
          aria-hidden
          onClick={close}
          className={cn(
            'lg:hidden fixed inset-0 z-[55] bg-background/70 backdrop-blur-sm transition-opacity duration-300 motion-reduce:transition-none',
            isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
          )}
        />
      )}

      <div
        ref={panelRef}
        id="auction-filters-panel"
        role={isSidebar ? undefined : 'dialog'}
        aria-modal={isSidebar ? undefined : true}
        aria-labelledby={isSidebar ? undefined : 'auction-filters-title'}
        inert={!isSidebar && !isOpen}
        onKeyDown={handleKeyDown}
        className={cn(
          // Drawer, below lg. `visibility` rides along so the closed drawer is
          // out of the tab order and the accessibility tree even before `inert`.
          // It has NO duration of its own: it flips to visible the instant the
          // drawer opens (a transitioned visibility is still `hidden` on the
          // first frame, and `focus()` into it silently fails) and, on close,
          // flips to hidden only after the slide has finished.
          'fixed inset-y-0 right-0 z-[60] flex w-[min(100%,26rem)] flex-col overflow-y-auto border-l border-neutral-800 bg-background shadow-2xl transition-[transform,visibility] [transition-duration:300ms,0s] ease-out motion-reduce:transition-none',
          isOpen ? 'visible translate-x-0 delay-0' : 'invisible translate-x-full delay-[0s,300ms]',
          // Sidebar, lg and up: back in the column. The PINNING is the column's
          // (`StickyPanel` around header and form), so title and form move as one.
          'lg:visible lg:static lg:inset-auto lg:z-20 lg:w-auto lg:translate-x-0 lg:overflow-visible lg:border-0 lg:bg-transparent lg:shadow-none'
        )}
      >
        <div className="lg:hidden flex items-center justify-between border-b border-neutral-800 px-5 py-4">
          <h2 id="auction-filters-title" className="font-oswald text-xl uppercase tracking-wide">
            Filtry
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={close}
            aria-label="Zamknij filtry"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-neutral-700 text-neutral-300 transition-colors hover:border-action hover:text-action"
          >
            <X size={20} aria-hidden />
          </button>
        </div>

        <div className="p-4 lg:p-0">{children}</div>
      </div>
    </AuctionFilterHomeContext.Provider>
  );
};
